import type {
  QuarterData, ValidQuarter, CANSLIMScore, Signal, ShareholderDelta,
  ShareholdersSeries, ShareholderEntry,
} from './fundamentals.types';
import type { OHLCVBar } from '@/types';

/**
 * First and latest disclosed shareholder registers, "Others" (the synthetic
 * outside-top-20 residual row) stripped out since it isn't a real holder to
 * diff against. `shareholders` comes from /api/statements, a separate,
 * possibly-shorter series from the income-statement `quarters` these
 * functions otherwise key off — never assume the two share an index range.
 */
function firstLatestHolders(shareholders: ShareholdersSeries | null): { first: ShareholderEntry[]; latest: ShareholderEntry[] } {
  if (!shareholders || shareholders.quarters.length === 0) return { first: [], latest: [] };
  const firstQ = shareholders.quarters[0];
  const latestQ = shareholders.quarters[shareholders.quarters.length - 1];
  const strip = (entries: ShareholderEntry[]) => entries.filter((h) => h.name.toLowerCase() !== 'others');
  return {
    first: strip(shareholders.byQuarter[firstQ] ?? []),
    latest: strip(shareholders.byQuarter[latestQ] ?? []),
  };
}

// ─── Quarter filtering & ordering ────────────────────────────────────────────

const QUARTER_ORDER: Record<string, number> = {
  Q1: 1, Q2: 2, Q3: 3, Q4: 4,
};

const MONTH_TO_QUARTER: Record<string, string> = {
  Mar: 'Q1',
  Jun: 'Q2',
  Sep: 'Q3',
  Dec: 'Q4',
};

/** Returns only quarters with REVENUE > 1, sorted chronologically oldest→newest */
export function getValidQuarters(data: Record<string, QuarterData>): ValidQuarter[] {
  const entries = Object.entries(data).filter(([, q]) => {
    const rev = q.INCOME_STATEMENT.REVENUE;
    return rev !== null && rev > 1;
  });

  // Freshly-announced quarters sometimes reach the API with no fiscal_period
  // yet (backfill lag), while older quarters of the same symbol do carry one.
  // For a company whose fiscal year doesn't start in January, fiscal_period's
  // quarter number is offset from the calendar quarter implied by the label
  // (e.g. a "Jun" quarter tagged fiscal "Q1"), so falling back to a plain
  // calendar mapping for just the missing rows would tag them inconsistently
  // with their own same-month siblings and break prior-year matching below.
  // Learn each calendar month's fiscal quarter + year offset from whichever
  // rows do have fiscal_period, and reuse it for rows that don't.
  const fiscalByMonth = new Map<string, { quarter: string; yearOffset: number }>();
  for (const [label, q] of entries) {
    const fiscal = q.fiscal_period?.match(/^(\d{4})-Q([1-4])$/);
    if (!fiscal) continue;
    const monthLabel = label.split(' ')[0];
    if (fiscalByMonth.has(monthLabel)) continue;
    const calendarYear = parseInt(label.split(' ')[1], 10);
    if (Number.isNaN(calendarYear)) continue;
    fiscalByMonth.set(monthLabel, { quarter: `Q${fiscal[2]}`, yearOffset: parseInt(fiscal[1], 10) - calendarYear });
  }

  return entries
    .map(([label, q]) => {
      const fiscal = q.fiscal_period?.match(/^(\d{4})-Q([1-4])$/);
      if (fiscal) {
        const year = parseInt(fiscal[1], 10);
        const month = `Q${fiscal[2]}`;
        return { label, data: q, month, year };
      }

      const parts = label.split(' ');
      const monthLabel = parts[0];
      const calendarYear = parseInt(parts[1], 10);
      const inferred = fiscalByMonth.get(monthLabel);
      if (inferred && !Number.isNaN(calendarYear)) {
        return { label, data: q, month: inferred.quarter, year: calendarYear + inferred.yearOffset };
      }

      const month = MONTH_TO_QUARTER[monthLabel] ?? monthLabel;
      return { label, data: q, month, year: calendarYear };
    })
    .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return (QUARTER_ORDER[a.month] ?? 0) - (QUARTER_ORDER[b.month] ?? 0);
    });
}

// ─── Trailing metrics ─────────────────────────────────────────────────────────

/** Sum of EPS for the 4 quarters ending at `endIdx` (inclusive) */
export function getT4QEPS(quarters: ValidQuarter[], endIdx: number): number | null {
  if (endIdx < 3) return null;
  let sum = 0;
  for (let i = endIdx - 3; i <= endIdx; i++) {
    const eps = quarters[i].data.INCOME_STATEMENT.EPS;
    if (eps === null) return null;
    sum += eps;
  }
  return sum;
}

/** Sum of REVENUE for the 4 quarters ending at `endIdx` (inclusive) */
export function getT4QRevenue(quarters: ValidQuarter[], endIdx: number): number | null {
  if (endIdx < 3) return null;
  let sum = 0;
  for (let i = endIdx - 3; i <= endIdx; i++) {
    const rev = quarters[i].data.INCOME_STATEMENT.REVENUE;
    if (rev === null) return null;
    sum += rev;
  }
  return sum;
}

// ─── YoY helpers ─────────────────────────────────────────────────────────────

/** Find a quarter with a specific month label in the array */
export function findQuarterByMonth(
  quarters: ValidQuarter[],
  month: string,
  year: number,
): ValidQuarter | undefined {
  return quarters.find(q => q.month === month && q.year === year);
}

/** Compute YoY % change for EPS comparing same-quarter 1 year apart */
export function epsYoY(
  quarters: ValidQuarter[],
  targetMonth: string,
  targetYear: number,
): { current: number | null; prior: number | null; pct: number | null } {
  const current = findQuarterByMonth(quarters, targetMonth, targetYear);
  const prior   = findQuarterByMonth(quarters, targetMonth, targetYear - 1);
  const c = current?.data.INCOME_STATEMENT.EPS ?? null;
  const p = prior?.data.INCOME_STATEMENT.EPS ?? null;
  const pct = c !== null && p !== null && p !== 0 ? ((c - p) / Math.abs(p)) * 100 : null;
  return { current: c, prior: p, pct };
}

/** Compute YoY % change for REVENUE comparing same-quarter 1 year apart */
export function revenueYoY(
  quarters: ValidQuarter[],
  targetMonth: string,
  targetYear: number,
): { current: number | null; prior: number | null; pct: number | null } {
  const current = findQuarterByMonth(quarters, targetMonth, targetYear);
  const prior   = findQuarterByMonth(quarters, targetMonth, targetYear - 1);
  const c = current?.data.INCOME_STATEMENT.REVENUE ?? null;
  const p = prior?.data.INCOME_STATEMENT.REVENUE ?? null;
  const pct = c !== null && p !== null && p !== 0 ? ((c - p) / Math.abs(p)) * 100 : null;
  return { current: c, prior: p, pct };
}

/**
 * YoY comparison for the most recently reported quarter ending in a specific
 * calendar month (e.g. "Sep", "Dec"), matched against `label` directly rather
 * than `.month` — `.month` holds each company's own fiscal-quarter tag (e.g.
 * AEL's fiscal Q3 lands in December, not September), so a fixed calendar-month
 * lookup can't go through it.
 */
export function calendarMonthYoY(
  quarters: ValidQuarter[],
  calendarMonth: string,
  metric: 'EPS' | 'REVENUE',
): { current: number | null; prior: number | null; pct: number | null } {
  for (let i = quarters.length - 1; i >= 0; i--) {
    if (!quarters[i].label.startsWith(`${calendarMonth} `)) continue;
    const year = parseInt(quarters[i].label.split(' ')[1], 10);
    if (Number.isNaN(year)) break;
    const curr = quarters[i];
    const prior = quarters.find(q => q.label === `${calendarMonth} ${year - 1}`);
    const c = metric === 'EPS' ? curr.data.INCOME_STATEMENT.EPS : curr.data.INCOME_STATEMENT.REVENUE;
    const p = prior ? (metric === 'EPS' ? prior.data.INCOME_STATEMENT.EPS : prior.data.INCOME_STATEMENT.REVENUE) : null;
    const pct = c !== null && p !== null && p !== 0 ? ((c - p) / Math.abs(p)) * 100 : null;
    return { current: c, prior: p, pct };
  }
  return { current: null, prior: null, pct: null };
}

// ─── Q2Q growth series ────────────────────────────────────────────────────────

export interface Q2QPoint {
  label: string;
  pct: number | null;
}

export function q2qEPSGrowth(quarters: ValidQuarter[]): Q2QPoint[] {
  return quarters.slice(1).map((q, i) => {
    const curr = q.data.INCOME_STATEMENT.EPS;
    const prev = quarters[i].data.INCOME_STATEMENT.EPS;
    const pct = curr !== null && prev !== null && prev !== 0
      ? ((curr - prev) / Math.abs(prev)) * 100
      : null;
    return { label: q.label, pct };
  });
}

export function q2qRevenueGrowth(quarters: ValidQuarter[]): Q2QPoint[] {
  return quarters.slice(1).map((q, i) => {
    const curr = q.data.INCOME_STATEMENT.REVENUE;
    const prev = quarters[i].data.INCOME_STATEMENT.REVENUE;
    const pct = curr !== null && prev !== null && prev !== 0
      ? ((curr - prev) / Math.abs(prev)) * 100
      : null;
    return { label: q.label, pct };
  });
}

// ─── Historical trailing PER (needs a daily price series) ────────────────────

const CALENDAR_MONTH_NUM: Record<string, number> = { Mar: 3, Jun: 6, Sep: 9, Dec: 12 };

/** Close price nearest a quarter's calendar month-end, matched by `label` (a real calendar month) rather than the fiscal `.month` tag */
function closeAtQuarterEnd(bars: OHLCVBar[], label: string): number | null {
  const [monAbbr, yStr] = label.split(' ');
  const targetMonth = CALENDAR_MONTH_NUM[monAbbr];
  const targetYear = parseInt(yStr, 10);
  if (!targetMonth || Number.isNaN(targetYear)) return null;

  const monthStart = Date.UTC(targetYear, targetMonth - 1, 1) / 1000;
  const monthEnd = Date.UTC(targetYear, targetMonth, 1) / 1000;

  let inMonth: OHLCVBar | null = null;
  let before: OHLCVBar | null = null;
  for (const bar of bars) {
    if (bar.time >= monthStart && bar.time < monthEnd) {
      if (!inMonth || bar.time > inMonth.time) inMonth = bar;
    } else if (bar.time < monthStart) {
      if (!before || bar.time > before.time) before = bar;
    }
  }
  return (inMonth ?? before)?.close ?? null;
}

export interface TrailingPERPoint { label: string; per: number | null; }

/** Trailing PER as-of each quarter: that quarter's own T4Q EPS against the close price nearest its period end */
export function trailingPERSeries(quarters: ValidQuarter[], bars: OHLCVBar[]): TrailingPERPoint[] {
  if (bars.length === 0) return quarters.map(q => ({ label: q.label, per: null }));
  return quarters.map((q, idx) => {
    const t4q = idx >= 3 ? getT4QEPS(quarters, idx) : null;
    const close = closeAtQuarterEnd(bars, q.label);
    const per = t4q !== null && t4q > 0 && close !== null ? close / t4q : null;
    return { label: q.label, per };
  });
}

// ─── Forward PER computation ──────────────────────────────────────────────────

/**
 * Forward EPS as of a given quarter index (the same walk-forward logic used at
 * the latest quarter, generalized so it can also be replayed at any earlier
 * index to build a historical Forward PER series):
 * 1. Find the last 3 same-quarter appearances before `idx`
 * 2. Average their EPS → the quarter's own next-quarter estimate
 * 3. T4Q forward = the 3 quarters immediately before `idx` + that estimate
 */
export interface ForwardPERBasisRow { label: string; eps: number; }

interface ForwardEpsAsOf {
  estimate: number | null;
  t4qFwd: number | null;
  estimateBasis: ForwardPERBasisRow[];
  t4qFwdBasis: ForwardPERBasisRow[];
}

function forwardEpsAsOf(quarters: ValidQuarter[], idx: number): ForwardEpsAsOf {
  const empty: ForwardEpsAsOf = { estimate: null, t4qFwd: null, estimateBasis: [], t4qFwdBasis: [] };
  if (idx < 0 || idx >= quarters.length) return empty;

  const targetMonth = quarters[idx].month;
  const sameQtr = quarters.slice(0, idx).filter(q => q.month === targetMonth).slice(-3);
  const estimateBasis: ForwardPERBasisRow[] = sameQtr
    .filter(q => q.data.INCOME_STATEMENT.EPS !== null)
    .map(q => ({ label: q.label, eps: q.data.INCOME_STATEMENT.EPS as number }));
  if (estimateBasis.length === 0) return empty;

  const estimate = estimateBasis.reduce((a, b) => a + b.eps, 0) / estimateBasis.length;

  const last3 = quarters.slice(idx - 3, idx);
  const t4qFwdBasis: ForwardPERBasisRow[] = last3
    .filter(q => q.data.INCOME_STATEMENT.EPS !== null)
    .map(q => ({ label: q.label, eps: q.data.INCOME_STATEMENT.EPS as number }));
  if (t4qFwdBasis.length < 3) return { estimate, t4qFwd: null, estimateBasis, t4qFwdBasis };

  const t4qFwd = t4qFwdBasis.reduce((a, b) => a + b.eps, 0) + estimate;
  return { estimate, t4qFwd, estimateBasis, t4qFwdBasis };
}

export interface ForwardPERResult extends ForwardEpsAsOf {
  fwdPER: number | null;
}

export function computeForwardPER(
  quarters: ValidQuarter[],
  mktPrice: number,
): ForwardPERResult {
  if (quarters.length < 2) return { estimate: null, t4qFwd: null, fwdPER: null, estimateBasis: [], t4qFwdBasis: [] };
  const basis = forwardEpsAsOf(quarters, quarters.length - 1);
  const fwdPER = basis.t4qFwd !== null && basis.t4qFwd > 0 ? mktPrice / basis.t4qFwd : null;
  return { ...basis, fwdPER };
}

export interface EpsEstimateMethod {
  id: 'seasonal' | 'trailing' | 'growth';
  label: string;
  eps: number | null;
  detail: string;
}

/**
 * Three independent lenses on "next quarter's EPS" so a single point-estimate
 * isn't mistaken for consensus — how far apart they land is itself a signal:
 * - Seasonal avg: the existing Forward PER basis, unchanged — average of the
 *   last same-position quarters, ignores any growth trend entirely.
 * - Trailing avg: T4Q ÷ 4 — the recent run-rate, ignores seasonality entirely.
 * - YoY growth: the latest same-position quarter grown by the YoY rate
 *   between it and the one before — seasonal-consistent but trend-aware,
 *   unlike the flat Seasonal average.
 */
export function computeEpsEstimateMethods(quarters: ValidQuarter[]): EpsEstimateMethod[] {
  const idx = quarters.length - 1;
  const { estimate: seasonalEst, estimateBasis } = forwardEpsAsOf(quarters, idx);

  const t4q = quarters.length >= 4 ? getT4QEPS(quarters, idx) : null;
  const trailingEst = t4q !== null ? t4q / 4 : null;

  let growthEst: number | null = null;
  if (estimateBasis.length >= 2) {
    const recent = estimateBasis[estimateBasis.length - 1];
    const older = estimateBasis[estimateBasis.length - 2];
    if (older.eps !== 0) {
      growthEst = recent.eps * (1 + (recent.eps - older.eps) / Math.abs(older.eps));
    }
  }

  return [
    { id: 'seasonal', label: 'Seasonal avg', eps: seasonalEst, detail: `Avg of the last ${estimateBasis.length} same-position quarter${estimateBasis.length === 1 ? '' : 's'}` },
    { id: 'trailing', label: 'Trailing avg', eps: trailingEst, detail: 'Last 4 actual quarters, averaged (T4Q ÷ 4)' },
    { id: 'growth', label: 'YoY growth', eps: growthEst, detail: 'Latest same-position quarter grown by its own YoY rate' },
  ];
}

export interface ForwardPERPoint { label: string; per: number | null; }

/** Historical Forward PER as-of each quarter: that quarter's own walk-forward T4Q estimate against the close price nearest its period end */
export function forwardPERSeries(quarters: ValidQuarter[], bars: OHLCVBar[]): ForwardPERPoint[] {
  if (bars.length === 0) return quarters.map(q => ({ label: q.label, per: null }));
  return quarters.map((q, idx) => {
    const { t4qFwd } = forwardEpsAsOf(quarters, idx);
    const close = closeAtQuarterEnd(bars, q.label);
    const per = t4qFwd !== null && t4qFwd > 0 && close !== null ? close / t4qFwd : null;
    return { label: q.label, per };
  });
}

// ─── EPS Estimate Helpers (shared by floating widget & indicator) ─────────────

/**
 * Compute average EPS estimate for indicator/valuation series.
 * Walk-forward logic:
 * - Annualize released quarters in the current fiscal year (as-of latest quarter)
 * - Else: fall back to TTM EPS
 */
export function computeAvgEpsEstimate(quarters: ValidQuarter[]): number | null {
  const n = quarters.length;
  if (n === 0) return null;

  const MONTHS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
  const latest = quarters[n - 1];
  const targetYear = latest.year;

  // Check for released quarters in current year (as-of latest quarter)
  const releasedQs = MONTHS
    .map(m => quarters.find(x => x.month === m && x.year === targetYear))
    .filter((q): q is ValidQuarter => q !== undefined);

  if (releasedQs.length > 0) {
    const epsVals = releasedQs
      .map(q => q.data.INCOME_STATEMENT.EPS)
      .filter((v): v is number => v !== null);
    if (epsVals.length === releasedQs.length) {
      // All released quarters have EPS → annualize
      return epsVals.reduce((a, b) => a + b, 0) * (4 / releasedQs.length);
    }
  }

  // Fall back to TTM
  return n >= 4 ? getT4QEPS(quarters, n - 1) : null;
}

/**
 * Compute growth EPS estimate for indicator/valuation series.
 * Walk-forward logic:
 * - Use released quarters in the current year to compute avg YoY growth
 * - Project missing quarters in the current year using that avg YoY growth
 * - Else: apply 3-year avg annual growth to prior full year
 */
export function computeGrowthEpsEstimate(quarters: ValidQuarter[]): number | null {
  const n = quarters.length;
  if (n === 0) return null;

  const MONTHS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

  // Helper: sum EPS for a full calendar year
  function sumYearEPS(yr: number): number | null {
    let sum = 0;
    for (const m of MONTHS) {
      const q = quarters.find(x => x.month === m && x.year === yr);
      if (!q) return null;
      const v = q.data.INCOME_STATEMENT.EPS;
      if (v === null) return null;
      sum += v;
    }
    return sum;
  }

  // Helper: avg annual growth over N years
  function avgAnnualGrowth(baseYear: number): number | null {
    const rates: number[] = [];
    for (let i = 0; i < 3; i++) {
      const yr = baseYear - i;
      const cur = sumYearEPS(yr);
      const prior = sumYearEPS(yr - 1);
      if (cur !== null && prior !== null && prior !== 0) {
        rates.push(((cur - prior) / Math.abs(prior)) * 100);
      }
    }
    return rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
  }

  const latest = quarters[n - 1];
  const targetYear = latest.year;
  const priorYear = targetYear - 1;

  const releasedQs = MONTHS
    .map(m => quarters.find(x => x.month === m && x.year === targetYear))
    .filter((q): q is ValidQuarter => q !== undefined);

  if (releasedQs.length > 0) {
    // Compute YoY growth rates from released quarters
    const growthRates: number[] = [];
    for (const q of releasedQs) {
      const prior = quarters.find(x => x.month === q.month && x.year === priorYear);
      if (!prior) continue;
      const eps = q.data.INCOME_STATEMENT.EPS;
      const priorEps = prior.data.INCOME_STATEMENT.EPS;
      if (eps !== null && priorEps !== null && priorEps !== 0) {
        growthRates.push(((eps - priorEps) / Math.abs(priorEps)) * 100);
      }
    }

    let avgGrowth = growthRates.length > 0
      ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
      : null;

    // Sum released actuals + project missing quarters
    const releasedSum = releasedQs
      .map(q => q.data.INCOME_STATEMENT.EPS)
      .filter((v): v is number => v !== null)
      .reduce((a, b) => a + b, 0);

    // If full-year data is available, project next-year EPS using avg YoY growth
    if (releasedQs.length === 4 && avgGrowth !== null) {
      return releasedSum * (1 + avgGrowth / 100);
    }

    if (avgGrowth !== null) {
      const releasedMonths = new Set(releasedQs.map(q => q.month));
      const missingMonths = MONTHS.filter(m => !releasedMonths.has(m));
      let projectedSum = 0;

      for (const m of missingMonths) {
        const priorQ = quarters.find(x => x.month === m && x.year === priorYear);
        if (!priorQ) {
          avgGrowth = null;
          break;
        }
        const pv = priorQ.data.INCOME_STATEMENT.EPS;
        if (pv !== null) {
          projectedSum += pv * (1 + avgGrowth / 100);
        }
      }

      if (avgGrowth !== null) {
        return releasedSum + projectedSum;
      }
    }
  }

  // Fall back: apply 3-year avg growth to prior full year
  const priorYEPS = sumYearEPS(priorYear);
  if (priorYEPS !== null) {
    const avgGrowth = avgAnnualGrowth(priorYear);
    if (avgGrowth !== null) {
      return priorYEPS * (1 + avgGrowth / 100);
    }
  }

  return null;
}

// ─── CANSLIM scoring ──────────────────────────────────────────────────────────

export function computeCANSLIM(
  quarters: ValidQuarter[],
  marketScore: 1 | 3 | 5,
  shareholders: ShareholdersSeries | null = null,
): CANSLIMScore {
  const rationale: Record<string, string> = {};
  const n = quarters.length;
  const { first: firstHolders, latest: latestHolders10 } = firstLatestHolders(shareholders);

  // C — Current quarterly EPS growth
  let C = 1;
  if (n >= 2) {
    const curr = quarters[n - 1].data.INCOME_STATEMENT.EPS;
    const prev = quarters[n - 2].data.INCOME_STATEMENT.EPS;
    if (curr !== null && prev !== null && prev !== 0) {
      const pct = ((curr - prev) / Math.abs(prev)) * 100;
      if (pct > 20) { C = 5; rationale.C = `EPS up ${pct.toFixed(1)}% QoQ (>20%).`; }
      else if (pct >= -5) { C = 3; rationale.C = `EPS changed ${pct.toFixed(1)}% QoQ (within range).`; }
      else { C = 1; rationale.C = `EPS declined ${pct.toFixed(1)}% QoQ.`; }
    } else { rationale.C = 'Insufficient EPS data.'; }
  } else { rationale.C = 'Insufficient quarters.'; }

  // A — Annual EPS trend (trailing 4Q vs prior 4Q)
  let A = 1;
  if (n >= 8) {
    const t4q = getT4QEPS(quarters, n - 1);
    const prior4q = getT4QEPS(quarters, n - 5);
    if (t4q !== null && prior4q !== null && prior4q !== 0) {
      const pct = ((t4q - prior4q) / Math.abs(prior4q)) * 100;
      if (pct > 20) { A = 5; rationale.A = `T4Q EPS up ${pct.toFixed(1)}% vs prior year.`; }
      else if (pct > 0) { A = 3; rationale.A = `T4Q EPS up ${pct.toFixed(1)}% vs prior year.`; }
      else { A = 1; rationale.A = `T4Q EPS declined ${Math.abs(pct).toFixed(1)}% vs prior year.`; }
    } else { rationale.A = 'Insufficient data for annual comparison.'; }
  } else { rationale.A = 'Need 8 quarters for annual comparison.'; }

  // N — New shareholders in latest top-10 vs first available
  let N = 1;
  if (shareholders && shareholders.quarters.length >= 2) {
    const firstNames = new Set(firstHolders.slice(0, 10).map(s => s.name));
    const latestNames = latestHolders10.slice(0, 10).map(s => s.name);
    const newCount = latestNames.filter(name => !firstNames.has(name)).length;
    if (newCount >= 2) { N = 5; rationale.N = `${newCount} new names in top-10 shareholders.`; }
    else if (newCount === 1) { N = 3; rationale.N = `1 new name in top-10 shareholders.`; }
    else { N = 1; rationale.N = `No new names in top-10 shareholders.`; }
  } else { rationale.N = 'Shareholder register not available yet for comparison.'; }

  // S — Supply/Free float
  let S = 1;
  const ff = quarters[n - 1].data.INCOME_STATEMENT.FREE_FLOAT;
  if (ff !== null) {
    if (ff > 40) { S = 5; rationale.S = `Free float ${ff.toFixed(1)}% (>40%, good liquidity).`; }
    else if (ff >= 20) { S = 3; rationale.S = `Free float ${ff.toFixed(1)}% (20–40%, moderate).`; }
    else { S = 1; rationale.S = `Free float ${ff.toFixed(1)}% (<20%, low liquidity).`; }
  } else { rationale.S = 'Free float data not available.'; }

  // L — Leader or laggard (ROE)
  let L = 1;
  const roe = quarters[n - 1].data.INCOME_STATEMENT.ROE;
  if (roe !== null) {
    if (roe > 8) { L = 5; rationale.L = `ROE ${roe.toFixed(1)}% (>8%, strong returns).`; }
    else if (roe >= 3) { L = 3; rationale.L = `ROE ${roe.toFixed(1)}% (3–8%, moderate).`; }
    else { L = 1; rationale.L = `ROE ${roe.toFixed(1)}% (<3%, weak returns).`; }
  } else { rationale.L = 'ROE data not available.'; }

  // I — Institutional sponsorship (non-promoter, non-government holders in top-10)
  let I = 1;
  if (shareholders && latestHolders10.length > 0) {
    const promoterKeywords = ['perera', 'joshua', 'lankem', 'malalasekera', 'senthil', 'access international', 'foresight', 'confab'];
    const govKeywords = ["employee's provident", 'epf', 'etf', 'insurance corporation', 'government', 'treasury', 'ceylon petroleum'];
    const instCount = latestHolders10.slice(0, 10).filter(h => {
      const lname = h.name.toLowerCase();
      const isPromoter = promoterKeywords.some(kw => lname.includes(kw));
      const isGov = govKeywords.some(kw => lname.includes(kw));
      return !isPromoter && !isGov;
    }).length;
    if (instCount >= 4) { I = 5; rationale.I = `${instCount} institutional/minority holders in top-10.`; }
    else if (instCount >= 2) { I = 3; rationale.I = `${instCount} institutional/minority holders in top-10.`; }
    else { I = 1; rationale.I = `${instCount} institutional/minority holders in top-10.`; }
  } else { rationale.I = 'Shareholder register not available yet.'; }

  // M — Market direction (user-supplied)
  const M = marketScore;
  rationale.M = M === 5 ? 'Market is in confirmed uptrend.' : M === 3 ? 'Market in correction or mixed.' : 'Market in downtrend.';

  const total = C + A + N + S + L + I + M;
  return { C, A, N, S, L, I, M, total, rationale };
}

// ─── Signals ─────────────────────────────────────────────────────────────────

export function computeSignals(quarters: ValidQuarter[], mktPrice: number, shareholders: ShareholdersSeries | null = null): Signal[] {
  const n = quarters.length;
  const latest = quarters[n - 1]?.data.INCOME_STATEMENT;
  const prev   = n >= 2 ? quarters[n - 2].data.INCOME_STATEMENT : null;
  const signals: Signal[] = [];

  // EPS momentum
  if (latest && prev && latest.EPS !== null && prev.EPS !== null) {
    const diff = latest.EPS - prev.EPS;
    if (diff > 0.05) signals.push({ label: 'EPS Momentum', status: 'positive', badge: 'Rising' });
    else if (diff < -0.05) signals.push({ label: 'EPS Momentum', status: 'negative', badge: 'Declining' });
    else signals.push({ label: 'EPS Momentum', status: 'neutral', badge: 'Flat' });
  } else {
    signals.push({ label: 'EPS Momentum', status: 'neutral', badge: 'N/A' });
  }

  // NAV trend
  if (latest && prev && latest.NAVPS !== null && prev.NAVPS !== null) {
    signals.push(latest.NAVPS > prev.NAVPS
      ? { label: 'NAV Trend', status: 'positive', badge: 'Improving' }
      : { label: 'NAV Trend', status: 'negative', badge: 'Declining' }
    );
  } else {
    signals.push({ label: 'NAV Trend', status: 'neutral', badge: 'N/A' });
  }

  // Revenue trend
  if (latest && prev && latest.REVENUE !== null && prev.REVENUE !== null) {
    signals.push(latest.REVENUE > prev.REVENUE
      ? { label: 'Revenue Trend', status: 'positive', badge: 'Growing' }
      : { label: 'Revenue Trend', status: 'negative', badge: 'Shrinking' }
    );
  } else {
    signals.push({ label: 'Revenue Trend', status: 'neutral', badge: 'N/A' });
  }

  // Free cash flow
  const fcf = quarters[n - 1]?.data.CASH_FLOW.FREE_CASH_FLOW;
  if (fcf !== null && fcf !== undefined) {
    signals.push(fcf >= 0
      ? { label: 'Free Cash Flow', status: 'positive', badge: 'Positive' }
      : { label: 'Free Cash Flow', status: 'negative', badge: 'Negative' }
    );
  } else {
    signals.push({ label: 'Free Cash Flow', status: 'neutral', badge: 'N/A' });
  }

  // Gearing trend
  if (latest && prev && latest.GEARING !== null && prev.GEARING !== null) {
    signals.push(latest.GEARING < prev.GEARING
      ? { label: 'Gearing Trend', status: 'positive', badge: 'Improving' }
      : { label: 'Gearing Trend', status: 'negative', badge: 'Rising' }
    );
  } else {
    signals.push({ label: 'Gearing Trend', status: 'neutral', badge: 'N/A' });
  }

  // New major shareholder entry (top 20)
  if (shareholders && shareholders.quarters.length >= 2) {
    const { first, latest: latestHolders } = firstLatestHolders(shareholders);
    const firstNames = new Set(first.slice(0, 20).map(s => s.name));
    const newEntrants = latestHolders.slice(0, 20).filter(s => !firstNames.has(s.name));
    if (newEntrants.length > 0) {
      signals.push({ label: 'New Major Shareholder', status: 'positive', badge: `${newEntrants.length} New` });
    } else {
      signals.push({ label: 'New Major Shareholder', status: 'neutral', badge: 'None' });
    }
  } else {
    signals.push({ label: 'New Major Shareholder', status: 'neutral', badge: 'N/A' });
  }

  // Forward PER zone (requires mktPrice)
  const t4qEPS = n >= 4 ? getT4QEPS(quarters, n - 1) : null;
  if (t4qEPS !== null && t4qEPS > 0 && mktPrice > 0) {
    const per = mktPrice / t4qEPS;
    if (per < 8) signals.push({ label: 'Forward PER Zone', status: 'positive', badge: `${per.toFixed(1)}× (Low)` });
    else if (per <= 15) signals.push({ label: 'Forward PER Zone', status: 'neutral', badge: `${per.toFixed(1)}× (Fair)` });
    else signals.push({ label: 'Forward PER Zone', status: 'negative', badge: `${per.toFixed(1)}× (High)` });
  } else {
    signals.push({ label: 'Forward PER Zone', status: 'neutral', badge: 'N/A' });
  }

  // ROE direction
  if (latest && prev && latest.ROE !== null && prev.ROE !== null) {
    signals.push(latest.ROE > prev.ROE
      ? { label: 'ROE Direction', status: 'positive', badge: 'Improving' }
      : { label: 'ROE Direction', status: 'negative', badge: 'Declining' }
    );
  } else {
    signals.push({ label: 'ROE Direction', status: 'neutral', badge: 'N/A' });
  }

  return signals.slice(0, 8);
}

// ─── Shareholder comparison ───────────────────────────────────────────────────

export function computeShareholderDeltas(shareholders: ShareholdersSeries | null): ShareholderDelta[] {
  if (!shareholders || shareholders.quarters.length < 2) return [];

  const { first, latest } = firstLatestHolders(shareholders);

  const firstMap  = new Map(first.map(s => [s.name, s.pct]));
  const latestMap = new Map(latest.map(s => [s.name, s.pct]));

  const allNames = new Set([
    ...first.slice(0, 20).map(s => s.name),
    ...latest.slice(0, 20).map(s => s.name),
  ]);

  const deltas: ShareholderDelta[] = [];
  allNames.forEach(name => {
    const fp = firstMap.get(name) ?? null;
    const lp = latestMap.get(name) ?? null;

    let change: ShareholderDelta['change'] = 'unchanged';
    const delta = fp !== null && lp !== null ? lp - fp : null;

    if (fp === null && lp !== null) change = 'new';
    else if (fp !== null && lp === null) change = 'exited';
    else if (delta !== null && delta > 2) change = 'increased';
    else if (delta !== null && delta < -2) change = 'decreased';

    deltas.push({ name, firstPct: fp, latestPct: lp, change, delta });
  });

  // Sort: latest % desc (nulls last), then by change type priority
  return deltas.sort((a, b) => {
    const lp = (b.latestPct ?? 0) - (a.latestPct ?? 0);
    if (lp !== 0) return lp;
    return (a.latestPct ?? 0) - (b.latestPct ?? 0);
  });
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

export function fmt(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtM(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const m = v / 1_000_000;
  return m >= 1000
    ? `${(m / 1000).toFixed(2)}B`
    : `${m.toFixed(2)}M`;
}

/** Format a raw LKR value compactly: 16586197751 → "Rs 16.59B" */
export function fmtRevenue(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `Rs ${fmtM(v)}`;
}

export function pctChange(curr: number | null, prev: number | null): number | null {
  if (curr === null || prev === null || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

export function pctChangeLabel(pct: number | null): string {
  if (pct === null) return '';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}
